import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Bot,
    ChevronDown,
    ChevronUp,
    Loader2,
    Percent,
    RefreshCw,
    Scale,
    Sparkles,
    Users,
    Gavel,
    FileWarning,
} from "lucide-react";
import { toast } from "sonner";
import { akilDosyaIntel, akilDosyaIntelAction, akilUltraServicesAction } from "@/lib/akilApi";

function SkorHalka({ yuzde, label, tone = "gold" }) {
    const ring =
        yuzde >= 70
            ? "border-emerald-500/50 text-emerald-300"
            : yuzde >= 45
              ? "border-amber-500/50 text-amber-200"
              : "border-rose-500/50 text-rose-300";
    const bg =
        tone === "violet" ? "from-violet-500/10 to-transparent" : "from-[#D4AF37]/10 to-transparent";
    return (
        <div className={`rounded-2xl border bg-gradient-to-br p-4 ${bg} ${ring.split(" ")[0]} border-opacity-40`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
            <div className={`mt-2 flex items-center gap-3`}>
                <div
                    className={`flex h-14 w-14 items-center justify-center rounded-full border-2 font-display text-xl font-bold ${ring}`}
                >
                    {yuzde ?? "—"}%
                </div>
                {tone === "violet" ? <Bot className="h-5 w-5 text-violet-400/70" /> : <Percent className="h-5 w-5 text-[#D4AF37]/70" />}
            </div>
        </div>
    );
}

export default function DosyaIntelPanel({ dosyaId, compact = false }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [expanded, setExpanded] = useState(!compact);
    const [showAjan, setShowAjan] = useState(false);
    const [showPazarlik, setShowPazarlik] = useState(false);
    const [itirazBusy, setItirazBusy] = useState(false);
    const [itirazDraft, setItirazDraft] = useState(null);

    const yukle = useCallback(
        async (auto = true, silent = false) => {
            if (!dosyaId) return;
            if (!silent) setLoading(true);
            else setRefreshing(true);
            try {
                const r = await akilDosyaIntel(dosyaId, { auto: auto ? "1" : "0" });
                setData((prev) => {
                    const waitingTahmin =
                        Boolean(r?.queued?.tahmin) || (Boolean(prev?.queued?.tahmin) && !r?.tahmin);
                    const waitingStrateji =
                        Boolean(r?.queued?.strateji) || (Boolean(prev?.queued?.strateji) && !r?.strateji);
                    return {
                        ...r,
                        queued: {
                            tahmin: waitingTahmin,
                            strateji: waitingStrateji,
                        },
                    };
                });
                if (r?.queued?.strateji || r?.queued?.tahmin) {
                    toast.message("AI analiz kuyruğa alındı", {
                        description: "Strateji ve tahmin birkaç dakika içinde hazır olacak.",
                    });
                }
            } catch (e) {
                toast.error(e?.response?.data?.error || "AI istihbarat yüklenemedi");
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [dosyaId]
    );

    useEffect(() => {
        yukle(true);
    }, [yukle]);

    useEffect(() => {
        if (!data?.queued?.strateji && !data?.queued?.tahmin) return undefined;
        const t = setInterval(() => yukle(false, true), 12000);
        return () => clearInterval(t);
    }, [data?.queued, yukle]);

    const tetikle = async (action) => {
        setRefreshing(true);
        try {
            const r = await akilDosyaIntelAction({ dosya_id: dosyaId, action, force: true });
            if (r?.queued) {
                toast.message("AI analiz kuyruğa alındı", {
                    description: "Sonuç birkaç dakika içinde hazır olacak.",
                });
                setData((prev) => ({
                    ...(prev || {}),
                    queued: {
                        ...(prev?.queued || {}),
                        [action]: true,
                    },
                }));
            } else {
                toast.success("AI analiz güncellendi");
            }
            if (!r?.queued) {
                await yukle(false, true);
            }
        } catch (e) {
            toast.error(e?.response?.data?.error || "İşlem başarısız");
        } finally {
            setRefreshing(false);
        }
    };

    if (!dosyaId) return null;

    if (loading && !data) {
        return (
            <div className="flex items-center gap-2 rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 px-4 py-3 text-sm text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" />
                Akıl dosya istihbaratı yükleniyor…
            </div>
        );
    }

    const tetikleItiraz = async () => {
        setItirazBusy(true);
        try {
            const r = await akilUltraServicesAction({ action: "sanal_katip", dosya_id: dosyaId });
            if (r?.itiraz_dilekce_taslagi) {
                setItirazDraft(r.itiraz_dilekce_taslagi);
                const kaynak = r?.kaynak === "evrak" ? "Gerçek evrak" : r?.kaynak === "simulasyon" ? "Simülasyon" : "";
                toast.success(kaynak ? `Bilirkişi itiraz taslağı hazır (${kaynak})` : "Bilirkişi itiraz taslağı hazır");
            } else if (r?.bulgular?.analiz) {
                toast.message(r.bulgular.analiz.slice(0, 120));
            } else {
                toast.message(r?.message || "Sanal katip analizi tamamlandı");
            }
        } catch (e) {
            toast.error(e?.response?.data?.error || "İtiraz üretilemedi");
        } finally {
            setItirazBusy(false);
        }
    };

    const tahmin = data?.tahmin;
    const strateji = data?.strateji;
    const pazarlik = data?.pazarlik;
    const karar = data?.karar_mercii;
    const dk = data?.deger_kaybi;

    return (
        <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#121A2F]/90 to-[#0A0F1C]/80 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/[0.06] px-4 py-3">
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="flex items-center gap-2 text-left"
                >
                    <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                    <span className="font-display text-sm font-semibold text-[#F3E5AB]">Akıl Dosya İstihbarat</span>
                    {data?.ai_motor === "mbs_native" ? (
                        <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-300 border border-emerald-500/30">
                            MBS Native
                        </span>
                    ) : data?.ai_status?.gemini_configured ? (
                        <span className="rounded bg-sky-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-sky-300 border border-sky-500/20">
                            Gemini
                        </span>
                    ) : null}
                    {expanded ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
                </button>
                <div className="flex gap-1.5">
                    <button
                        type="button"
                        disabled={refreshing}
                        onClick={() => yukle(true, true)}
                        className="rounded-lg px-2 py-1 text-[10px] font-semibold text-slate-400 hover:bg-white/5"
                    >
                        {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    </button>
                    <Link
                        to="/yeni/supreme-ops"
                        className="rounded-lg bg-white/5 px-2 py-1 text-[10px] font-semibold text-[#D4AF37]/80 hover:bg-white/10"
                    >
                        War Room
                    </Link>
                </div>
            </div>

            {expanded ? (
                <div className="space-y-4 p-4">
                    <div className="grid gap-3 sm:grid-cols-3">
                        <SkorHalka
                            yuzde={tahmin?.kazanma_yuzde}
                            label="Kazanma ihtimali"
                        />
                        <SkorHalka
                            yuzde={tahmin?.guven_skoru}
                            label="Güven skoru"
                            tone="violet"
                        />
                        <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tahmini tutar</div>
                            <div className="mt-2 text-sm font-semibold text-slate-200">
                                {tahmin
                                    ? `₺${(tahmin.tahmini_tutar_min || 0).toLocaleString("tr-TR")} – ₺${(tahmin.tahmini_tutar_max || 0).toLocaleString("tr-TR")}`
                                    : "—"}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                                {tahmin
                                    ? `${tahmin.tahmini_sure_gun_min}–${tahmin.tahmini_sure_gun_max} gün`
                                    : "Tahmin bekleniyor"}
                            </div>
                        </div>
                    </div>

                    {karar ? (
                        <div
                            className={`rounded-xl border p-3 ${
                                karar.oneri === "tahkim"
                                    ? "border-emerald-500/30 bg-emerald-500/5"
                                    : karar.oneri === "uzlas"
                                      ? "border-amber-500/30 bg-amber-500/5"
                                      : "border-slate-500/30 bg-white/[0.03]"
                            }`}
                        >
                            <div className="flex items-center gap-2 text-sm font-semibold text-white">
                                <Gavel className="h-4 w-4 text-[#D4AF37]" />
                                Karar mercii · {karar.oneri_etiket}
                            </div>
                            <p className="text-xs text-slate-400 mt-2 leading-relaxed">{karar.aciklama}</p>
                            {dk?.hesaplanan_tutar ? (
                                <p className="text-[11px] text-[#F3E5AB] mt-2">
                                    DK: ₺{dk.hesaplanan_tutar.toLocaleString("tr-TR")} · öğrenme çarpanı{" "}
                                    {dk.ogrenme_carpani?.toFixed(2)}
                                </p>
                            ) : (
                                <p className="text-[11px] text-amber-400/90 mt-2 flex items-center gap-1">
                                    <FileWarning className="h-3 w-3" />
                                    Değer kaybı hesabı eksik — tahmin güvenilirliği düşük
                                </p>
                            )}
                        </div>
                    ) : null}

                    {(data?.queued?.tahmin || data?.queued?.strateji) && (
                        <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2 text-xs text-amber-200/90 space-y-1">
                            <p className="font-semibold">AI arka planda çalışıyor</p>
                            {data?.queued?.tahmin ? (
                                <p>
                                    • Sonuç tahmini
                                    {data?.job_status?.tahmin?.started_at
                                        ? ` — başlangıç ${new Date(data.job_status.tahmin.started_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}`
                                        : ""}
                                </p>
                            ) : null}
                            {data?.queued?.strateji ? (
                                <p>
                                    • Strateji raporu
                                    {data?.job_status?.strateji?.started_at
                                        ? ` — başlangıç ${new Date(data.job_status.strateji.started_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}`
                                        : ""}
                                </p>
                            ) : null}
                            <p className="text-[10px] text-slate-500">Sayfa otomatik yenilenecek (≈12 sn).</p>
                        </div>
                    )}

                    {tahmin?.rapor_metin && !String(tahmin.rapor_metin).toUpperCase().startsWith("HATA:") ? (
                        <p className="text-sm leading-relaxed text-slate-400">{tahmin.rapor_metin}</p>
                    ) : null}

                    {data?.ai_hata ? (
                        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100/90">
                            <p className="font-semibold">Eski AI kaydı geçersiz</p>
                            <p className="mt-1 text-amber-100/80">{data.ai_hata}</p>
                            <button
                                type="button"
                                className="mt-2 text-[10px] font-bold text-[#D4AF37] hover:underline"
                                onClick={() => yukle(true, true)}
                            >
                                MBS Akıl ile yeniden üret →
                            </button>
                        </div>
                    ) : null}

                    {tahmin?.riskler?.length ? (
                        <ul className="text-xs text-rose-300/90 space-y-1">
                            {tahmin.riskler.slice(0, 4).map((r, i) => (
                                <li key={i}>• {r}</li>
                            ))}
                        </ul>
                    ) : null}

                    {strateji ? (
                        <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm font-semibold text-violet-200">
                                    <Users className="h-4 w-4" />
                                    Çoklu ajan stratejisi
                                    <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px]">{strateji.durum}</span>
                                </div>
                                <button type="button" onClick={() => setShowAjan((v) => !v)} className="text-[10px] text-slate-500">
                                    {showAjan ? "Gizle" : "Tartışma"}
                                </button>
                            </div>
                            {strateji.onerilen_aksiyon?.baslik ? (
                                <p className="mt-2 text-xs text-slate-400">{strateji.onerilen_aksiyon.baslik}</p>
                            ) : null}
                            {showAjan && strateji.ajan_tartismalari?.length ? (
                                <div className="mt-2 max-h-40 space-y-2 overflow-y-auto">
                                    {strateji.ajan_tartismalari.map((t, i) => (
                                        <div key={i} className="rounded-lg bg-black/30 px-2 py-1.5 text-xs text-slate-400">
                                            <strong className="text-violet-300">{t.ajan}:</strong> {t.mesaj}
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                            {strateji.durum === "ONAY_BEKLIYOR" ? (
                                <Link
                                    to="/yeni/supreme-ops"
                                    className="mt-2 inline-block text-[10px] font-bold text-emerald-400 hover:underline"
                                >
                                    Supreme Ops → Onay Kapısı
                                </Link>
                            ) : null}
                        </div>
                    ) : null}

                    {pazarlik ? (
                        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm font-semibold text-amber-200">
                                    <Scale className="h-4 w-4" />
                                    Pazarlık asistanı · {pazarlik.sigorta_sirketi}
                                </div>
                                <button type="button" onClick={() => setShowPazarlik((v) => !v)} className="text-[10px] text-slate-500">
                                    {showPazarlik ? "Gizle" : "Teklif"}
                                </button>
                            </div>
                            <p className="mt-1 text-xs text-[#F3E5AB]">
                                Önerilen mutabakat: ₺{(pazarlik.tavsiye_edilen_teklif || 0).toLocaleString("tr-TR")}
                            </p>
                            {showPazarlik && pazarlik.teklif_onizleme ? (
                                <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap text-xs text-slate-400">
                                    {pazarlik.teklif_onizleme}
                                </pre>
                            ) : null}
                        </div>
                    ) : null}

                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            disabled={refreshing}
                            onClick={() => tetikle("tahmin")}
                            className="rounded-lg bg-[#D4AF37]/15 px-3 py-1.5 text-[11px] font-semibold text-[#F3E5AB] hover:bg-[#D4AF37]/25 disabled:opacity-50"
                        >
                            Tahmini yenile
                        </button>
                        <button
                            type="button"
                            disabled={refreshing}
                            onClick={() => tetikle("strateji")}
                            className="rounded-lg bg-violet-500/15 px-3 py-1.5 text-[11px] font-semibold text-violet-200 hover:bg-violet-500/25 disabled:opacity-50"
                        >
                            Strateji raporu üret
                        </button>
                        <button
                            type="button"
                            disabled={itirazBusy}
                            onClick={tetikleItiraz}
                            className="rounded-lg bg-rose-500/15 px-3 py-1.5 text-[11px] font-semibold text-rose-200 hover:bg-rose-500/25 disabled:opacity-50 inline-flex items-center gap-1"
                        >
                            {itirazBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                            Bilirkişi itiraz taslağı
                        </button>
                    </div>
                    {itirazDraft ? (
                        <pre className="text-xs text-slate-400 whitespace-pre-wrap max-h-48 overflow-y-auto bg-black/30 rounded-lg p-3 border border-rose-500/20">
                            {itirazDraft}
                        </pre>
                    ) : null}
                </div>
            ) : (
                <div className="flex items-center gap-4 px-4 py-3 text-sm">
                    <span className="text-emerald-300 font-bold">{tahmin?.kazanma_yuzde ?? "—"}%</span>
                    <span className="text-slate-500">kazanma</span>
                    {strateji?.durum === "ONAY_BEKLIYOR" ? (
                        <span className="text-violet-300 text-xs">· Ajan onayı bekliyor</span>
                    ) : null}
                </div>
            )}
        </div>
    );
}
